import type { IJobDefinition, IWorkflowInput } from '@escloud/types';
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { ImagingStep } from './imaging-step';
import { extractPatientPrefill } from './patient-details-step';
import { PatientDetailsStep } from './patient-details-step';
import { SlotProgress, type SlotDescriptor } from './slot-card';
import { useWizardSubmit } from './use-wizard-submit';
import { STEP_ORDER, WizardStepIndicator, type WizardStep } from './wizard-step-indicator';

import { Page, PageBody, PageFooter, PageHeader, ShellHeader } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useTenant } from '@/context';
import { useWorkflow } from '@/hooks';
import type { DicomMetadata } from '@/lib/dicom';

/**
 * Wizard-wide state shared across steps.
 *
 * - `slotPicks`: workflow slot key → bound InputDataset id. Both
 *   catalogue picks AND eager-uploaded reconstructions land here —
 *   reconstruction streams bytes to the server the moment the worker
 *   finishes, so by the time the user reaches step 2 there's only
 *   ever one shape of "filled" state per slot.
 * - `lockedPatient`: captured when the first slot is picked; the
 *   `id` constrains subsequent pick queries server-side, the `name`
 *   feeds the lock banner without depending on the current result
 *   list (which may be empty during a query roundtrip, or filtered
 *   to exclude the picked row). Released by "Change patient" (which
 *   also resets all picks).
 * - `patientDetails`: filled in step 2.
 */
export interface WizardState {
    slotPicks: Record<string, string>;
    lockedPatient: { id: string; name: string | null } | null;
    patientDetails: {
        name: string;
        lastname: string;
        firstname: string;
        dob: string;
        gender: string;
    };
}

const INITIAL_PATIENT_DETAILS: WizardState['patientDetails'] = {
    name: '',
    lastname: '',
    firstname: '',
    dob: '',
    gender: '',
};

const INITIAL_WIZARD_STATE: WizardState = {
    slotPicks: {},
    lockedPatient: null,
    patientDetails: INITIAL_PATIENT_DETAILS,
};

export function CreateCaseWizard() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const workflowId = searchParams.get('workflow');
    const { tenantId } = useTenant();
    const { data: workflow, isLoading, error } = useWorkflow(workflowId ?? undefined);

    const [step, setStep] = useState<WizardStep>('imaging');
    const [wizardState, setWizardState] = useState<WizardState>(INITIAL_WIZARD_STATE);
    const { submit, isSubmitting, currentStep, error: submitError } = useWizardSubmit();

    const volumeSlots: SlotDescriptor[] = useMemo(() => {
        if (!workflow?.inputs) return [];
        const inputs: Record<string, IWorkflowInput> = workflow.inputs;
        return Object.entries(inputs)
            .filter(([, def]) => def.type === 'VOLUME')
            .map(([key, definition]) => ({ key, definition }));
    }, [workflow]);

    // First job in the workflow is the one we queue after case
    // creation, matching the legacy CreateCasePage behaviour.
    const initialJob: { key: string; definition: IJobDefinition } | null = useMemo(() => {
        if (!workflow?.jobs) return null;
        const entries = Object.entries(workflow.jobs);
        if (entries.length === 0) return null;
        return { key: entries[0][0], definition: entries[0][1] };
    }, [workflow]);

    const currentIdx = STEP_ORDER.indexOf(step);
    const isFirstStep = currentIdx === 0;
    const isLastStep = currentIdx === STEP_ORDER.length - 1;

    // Imaging step is "ready" when every required VOLUME slot has a
    // bound InputDataset id in `slotPicks`. Eager upload means
    // reconstructions show up here too, so this single gate covers
    // both ingest paths. Phase 3 has no suitability scoring — the
    // surgeon confirms visually before clicking Continue.
    const canContinueFromImaging = useMemo(() => {
        const requiredSlots = volumeSlots.filter((s) => s.definition.required !== false);
        if (requiredSlots.length === 0) return true;
        return requiredSlots.every((s) => wizardState.slotPicks[s.key]);
    }, [volumeSlots, wizardState.slotPicks]);

    // Patient-details step gates on a non-empty case name. Other
    // patient fields are optional and accept whatever the user typed.
    const canContinueFromPatientDetails = wizardState.patientDetails.name.trim().length > 0;

    // First slot pick — used by the patient-details step to pre-fill
    // from DICOM Q/R when the user picked an existing dataset.
    const firstPickedDatasetId = useMemo(() => {
        for (const slot of volumeSlots) {
            const id = wizardState.slotPicks[slot.key];
            if (id) return id;
        }
        return null;
    }, [volumeSlots, wizardState.slotPicks]);

    const canContinue =
        step === 'imaging'
            ? canContinueFromImaging
            : step === 'patient-details'
              ? canContinueFromPatientDetails
              : true;

    const handleBack = () => {
        if (isSubmitting) return;
        if (isFirstStep) {
            navigate(`/tenants/${tenantId}`);
        } else {
            setStep(STEP_ORDER[currentIdx - 1]);
        }
    };

    const handleCreate = useCallback(async () => {
        if (!workflowId) return;
        const outcome = await submit({
            workflowId,
            initialJob,
            slots: volumeSlots,
            state: wizardState,
            onDatasetUnavailable: ({ slotKey }) => {
                // Sweeper reaped the picked dataset before atomic create
                // committed. Clear the affected slot (if we know which)
                // and route the user back to imaging so they can re-pick.
                setWizardState((prev) => {
                    if (!slotKey) return prev;
                    const nextPicks = { ...prev.slotPicks };
                    delete nextPicks[slotKey];
                    return { ...prev, slotPicks: nextPicks };
                });
                setStep('imaging');
            },
        });
        if (!outcome) return;

        if (outcome.jobError) {
            // Case was created (and counted against quota) but the
            // initial-job kickoff failed. Send the user back to the
            // tenant cases list — the case appears there in a pending
            // state where the user can retry — rather than leave them
            // staring at a generic error in the wizard.
            toast.warning('Case created, but processing did not start', {
                description: outcome.jobError.message,
            });
        } else {
            toast.success('Case created successfully');
        }
        navigate(`/tenants/${tenantId}`);
    }, [submit, workflowId, initialJob, volumeSlots, wizardState, navigate, tenantId]);

    const handleContinue = () => {
        if (isLastStep) {
            void handleCreate();
            return;
        }
        setStep(STEP_ORDER[currentIdx + 1]);
    };

    const handlePickChange = (
        slotKey: string,
        datasetId: string | null,
        patientId?: string | null,
        patientName?: string | null
    ) => {
        setWizardState((prev) => {
            const nextPicks = { ...prev.slotPicks };
            if (datasetId) {
                nextPicks[slotKey] = datasetId;
            } else {
                delete nextPicks[slotKey];
            }

            // Engage the patient lock on the first pick that carries a
            // non-null patientId. We never clear the lock on individual
            // slot clears — "Change patient" is the explicit release.
            let nextLock = prev.lockedPatient;
            if (datasetId && !nextLock && patientId) {
                nextLock = { id: patientId, name: patientName ?? null };
            }
            // If clearing emptied every pick, release the lock so the
            // user is free to start over with another patient.
            if (!datasetId && Object.keys(nextPicks).length === 0) {
                nextLock = null;
            }

            return { ...prev, slotPicks: nextPicks, lockedPatient: nextLock };
        });
    };

    const handleChangePatient = () => {
        setWizardState((prev) => ({
            ...prev,
            slotPicks: {},
            lockedPatient: null,
        }));
    };

    const handlePatientDetailsChange = (next: WizardState['patientDetails']) => {
        setWizardState((prev) => ({ ...prev, patientDetails: next }));
    };

    // Pre-fill patient details from DICOM metadata extracted by the
    // worker during reconstruction. Same "empty fields only" semantics
    // as the picked-dataset path in PatientDetailsStep.
    const workflowName = workflow?.name;
    const handleDicomMetadata = useCallback(
        (metadata: DicomMetadata) => {
            const prefill = extractPatientPrefill(
                {
                    patientName: metadata.patientName,
                    patientBirthDate: metadata.patientBirthDate,
                    patientSex: metadata.patientSex,
                },
                workflowName
            );
            setWizardState((prev) => {
                let dirty = false;
                const nextDetails = { ...prev.patientDetails };
                for (const [k, v] of Object.entries(prefill) as [
                    keyof WizardState['patientDetails'],
                    string | undefined,
                ][]) {
                    if (!v) continue;
                    if (nextDetails[k] && nextDetails[k].trim().length > 0) continue;
                    nextDetails[k] = v;
                    dirty = true;
                }
                if (!dirty) return prev;
                return { ...prev, patientDetails: nextDetails };
            });
        },
        [workflowName]
    );

    if (!workflowId) {
        return (
            <Page>
                <PageBody scroll="none" className="items-center justify-center text-sm">
                    <p className="text-muted-foreground">No workflow selected.</p>
                    <Button variant="link" onClick={() => navigate(`/tenants/${tenantId}`)}>
                        Go back
                    </Button>
                </PageBody>
            </Page>
        );
    }

    if (isLoading) {
        return (
            <Page>
                <PageBody scroll="none" className="items-center justify-center text-sm text-muted-foreground">
                    Loading workflow…
                </PageBody>
            </Page>
        );
    }

    if (error || !workflow) {
        return (
            <Page>
                <PageBody scroll="none" className="items-center justify-center text-sm">
                    <p className="text-destructive">Failed to load workflow.</p>
                    <Button variant="link" onClick={() => navigate(`/tenants/${tenantId}`)}>
                        Go back
                    </Button>
                </PageBody>
            </Page>
        );
    }

    const slotsCount = volumeSlots.length;

    return (
        <Page>
            {/* Eyebrow + step progress live in the shell's top bar —
                one chrome band instead of three (ESCLOUD-247). */}
            <ShellHeader>
                <Separator orientation="vertical" className="hidden h-4 md:block" />
                <p className="min-w-0 truncate text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Create case · {workflow.name}
                </p>
                <div className="flex-1" />
                <WizardStepIndicator compact current={step} />
            </ShellHeader>

            <PageHeader className="border-b bg-background px-6 py-3">
                <h1 className="text-lg font-semibold tracking-tight">{stepHeadline(step, slotsCount)}</h1>
            </PageHeader>

            <PageBody scroll="none" padded={false} className="px-4 pt-4">
                {isSubmitting || submitError ? (
                    <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4">
                        <div className="flex w-full max-w-2xl flex-col items-center gap-3 rounded-md border bg-card p-6">
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                            <p className="text-sm font-medium">{currentStep || 'Working…'}</p>
                        </div>
                        {submitError && (
                            <p className="w-full max-w-2xl rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                                {submitError}
                            </p>
                        )}
                    </div>
                ) : (
                    <>
                        {step === 'imaging' &&
                            (volumeSlots.length > 0 ? (
                                <ImagingStep
                                    workflowId={workflowId}
                                    slots={volumeSlots}
                                    slotPicks={wizardState.slotPicks}
                                    lockedPatient={wizardState.lockedPatient}
                                    onPickChange={handlePickChange}
                                    onDicomMetadata={handleDicomMetadata}
                                    onChangePatient={handleChangePatient}
                                />
                            ) : (
                                <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
                                    This workflow has no VOLUME inputs to fill.
                                </div>
                            ))}
                        {step === 'patient-details' && (
                            // Forms may legitimately exceed short
                            // viewports — this step scrolls internally
                            // while the footer stays pinned.
                            <div className="min-h-0 flex-1 overflow-y-auto">
                                <PatientDetailsStep
                                    value={wizardState.patientDetails}
                                    onChange={handlePatientDetailsChange}
                                    firstPickedDatasetId={firstPickedDatasetId}
                                    workflow={workflow}
                                    slots={volumeSlots}
                                    slotPicks={wizardState.slotPicks}
                                />
                            </div>
                        )}
                    </>
                )}
            </PageBody>

            {/* Wizard footer: progress + Continue */}
            <PageFooter className="flex items-center gap-3 px-6 py-3">
                {step === 'imaging' && slotsCount > 1 ? (
                    <SlotProgress slots={volumeSlots} slotPicks={wizardState.slotPicks} />
                ) : (
                    <div className="flex-1" />
                )}
                <div className="flex-1" />
                <Button variant="outline" onClick={handleBack} disabled={isSubmitting}>
                    {isFirstStep ? (
                        'Cancel'
                    ) : (
                        <>
                            <ArrowLeft className="mr-1 h-4 w-4" />
                            Back
                        </>
                    )}
                </Button>
                <Button onClick={handleContinue} disabled={!canContinue || isSubmitting}>
                    {isLastStep ? (
                        <>
                            {isSubmitting && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                            Create case
                        </>
                    ) : (
                        <>
                            Continue
                            <ArrowRight className="ml-1 h-4 w-4" />
                        </>
                    )}
                </Button>
            </PageFooter>
        </Page>
    );
}

function stepHeadline(step: WizardStep, slotsCount: number): string {
    switch (step) {
        case 'imaging':
            return slotsCount > 1
                ? `Fill the ${numberToWords(slotsCount)} imaging inputs this workflow needs`
                : 'Choose the imaging for this case';
        case 'patient-details':
            return 'Confirm patient details and create the case';
    }
}

function numberToWords(n: number): string {
    const words = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
    return words[n] ?? String(n);
}
